import {
  EmptyState,
  FlexColumn,
  Loading,
  MaterialIcon,
  useLoading,
  useModal,
  useMoment,
} from '@gorila-shared-ui/components';
import { Button } from 'baseui/button';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { ONLY_DATE_FORMAT } from '../../../constants/app';
import { FLEET_SERVICE_STATUS_LABELS } from '../../../constants/fleet';
import { TABLE_COLUMNS_NAME } from '../../../constants/fleetServices';
import { useFeedback } from '../../../hooks/useFeedback';
import { useStyles } from '../../../hooks/useStyles';
import useUpdateEffect from '../../../hooks/useUpdateEffect';
import { apiParamsState } from '../../../storage/app';
import { FleetService, FleetServiceJob } from '../../../types/fleet';
import { StyledPaginatedTable } from '../../shared/StyledPaginatedTable';
import { StyledTooltip } from '../../ui/StyledTooltip';
import TableEmptyActionCell from '../../ui/TableEmptyActionCell';
import { useFleetAssetServices } from '../hooks/useFleetAssetServices';
import FleetServiceJobDetailModal from '../services/jobs/FleetServiceJobDetailModal';

export function FleetAssetServiceList() {
  const { theme, css, classes } = useStyles();
  const apiParams = useRecoilValue(apiParamsState);
  const [assetServices, setAssetServices] = useState<FleetService[]>();
  const {
    page,
    onChangePage,
    loadFleetAssetServices,
    selectedAssetId,
    reload,
    filters: { configurationId, status },
  } = useFleetAssetServices();
  const [totalItems, setTotalItems] = useState(0);
  const { formatDateFromTs } = useMoment();
  const { loading, stopLoading, startLoading } = useLoading();
  const { showFailFeedback } = useFeedback();
  const [selectedServiceJob, setSelectedServiceJob] = useState<FleetServiceJob>();
  const { showModal, closeModal, openModal } = useModal();

  useUpdateEffect(() => {
    onChangePage(0);
  }, [reload, selectedAssetId, configurationId, status, apiParams?.clientId, apiParams?.subClientId]);

  useEffect(() => {
    if (page === 0) {
      onChangePage(1);
      return;
    }
    const loadData = async () => {
      startLoading();
      await loadAssetServices();
      stopLoading();
    };

    loadData();
  }, [page]);

  const loadAssetServices = async () => {
    if (!selectedAssetId) return;
    const { services, error, total } = await loadFleetAssetServices(selectedAssetId);
    if (error) {
      showFailFeedback(error);
      setAssetServices(undefined);
      setTotalItems(0);
    } else {
      setAssetServices(services);
      setTotalItems(total ?? 0);
    }
  };

  const onOpenServiceJobModal = (serviceJob: FleetServiceJob) => {
    setSelectedServiceJob(serviceJob);
    openModal();
  };

  const onCloseServiceJobModal = () => {
    closeModal();
    setSelectedServiceJob(undefined);
  };

  const formatOnlyDateFromTs = (ts: number) => {
    return formatDateFromTs(ts, ONLY_DATE_FORMAT);
  };

  if (!selectedAssetId) {
    return (
      <FlexColumn classNames={`${classes.flexFill} ${css({ overflow: 'hidden' })}`}>
        <EmptyState title="Selecciona un activo para listar sus servicios." />
      </FlexColumn>
    );
  }

  return (
    <FlexColumn classNames={`${classes.flexFill} ${css({ overflow: 'hidden' })}`}>
      {!assetServices && !loading && <EmptyState title="El activo seleccionado todavía no cuenta con servicios." />}
      {!assetServices && loading && <Loading />}
      {assetServices && (
        <>
          {!!assetServices?.length && (
            <StyledPaginatedTable
              columns={TABLE_COLUMNS_NAME}
              data={assetServices.map((assetService) => {
                const hasServiceJob = !!assetService.serviceJob;
                return {
                  items: [
                    assetService?.asset.vehicle?.licensePlate || assetService?.asset.container?.identifier || '-',
                    assetService?.asset.label || '-',
                    FLEET_SERVICE_STATUS_LABELS[assetService.status],
                    assetService.configuration?.name || '-',
                    assetService?.lastMaintenanceDate ? formatOnlyDateFromTs(+assetService?.lastMaintenanceDate) : '-',
                    assetService?.lastMaintenanceMileage?.toLocaleString() || '-',
                    assetService?.nextServiceDate ? formatOnlyDateFromTs(+assetService?.nextServiceDate) : '-',
                    hasServiceJob ? (
                      <StyledTooltip
                        content="Ver detalles de servicio"
                        showArrow={false}
                      >
                        <Button
                          size="mini"
                          shape="square"
                          onClick={() => onOpenServiceJobModal(assetService.serviceJob!)}
                          kind="tertiary"
                        >
                          <MaterialIcon
                            name="info"
                            size={theme.sizing.scale700}
                          />
                        </Button>
                      </StyledTooltip>
                    ) : (
                      <TableEmptyActionCell />
                    ),
                  ],
                };
              })}
              totalItems={totalItems}
              page={page}
              onPageChange={onChangePage}
              loading={loading}
            />
          )}
          {!assetServices.length && (
            <EmptyState
              title="No se encontraron resultados"
              description="Intenta con otra busqueda"
            />
          )}
        </>
      )}
      {selectedServiceJob && (
        <FleetServiceJobDetailModal
          isOpen={showModal}
          onClose={onCloseServiceJobModal}
          serviceJob={selectedServiceJob}
        />
      )}
    </FlexColumn>
  );
}
